import { Controller } from "@hotwired/stimulus";

const DETROIT_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Detroit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23"
});

const FRACTION_DIGITS = 5;
const ANCHOR_REFRESH_MS = 60_000;

function formatDetroitTime(epochMilliseconds) {
  const parts = DETROIT_TIME_FORMATTER.formatToParts(new Date(epochMilliseconds));
  const clock = Object.fromEntries(
    parts
      .filter((part) => ["hour", "minute", "second"].includes(part.type))
      .map((part) => [part.type, part.value])
  );

  return `${clock.hour}:${clock.minute}:${clock.second}`;
}

export default class extends Controller {
  static targets = ["whole", "digit"];

  connect() {
    this.isFrozen = false;
    this.renderFrame = this.renderFrame.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.syncAnchor();
    this.renderFrame();
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.cancelFrame();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.cancelFrame();
      this.setFrozenState(false);
      return;
    }

    this.syncAnchor();
    this.renderFrame();
  }

  hold(event) {
    if (!this.isActivationEvent(event)) return;
    if (event.type === "keydown") event.preventDefault();
    if (this.isFrozen) return;

    const frozenAt = this.currentEpochMilliseconds();
    this.setFrozenState(true);
    this.cancelFrame();
    this.renderTime(frozenAt, true);
  }

  release(event) {
    if (!this.isReleaseEvent(event) || !this.isFrozen) return;
    if (event.type === "keyup") event.preventDefault();

    this.setFrozenState(false);
    this.syncAnchor();
    this.accessibleSecond = undefined;
    this.renderFrame();
  }

  renderFrame() {
    if (this.isFrozen || document.hidden) return;

    const epochNowMs = this.currentEpochMilliseconds();
    this.renderTime(epochNowMs);
    this.frameRequest = window.requestAnimationFrame(this.renderFrame);
  }

  currentEpochMilliseconds() {
    let monotonicNow = performance.now();
    if (monotonicNow - this.anchorMonotonicMs >= ANCHOR_REFRESH_MS) {
      this.syncAnchor();
      monotonicNow = performance.now();
    }

    return this.anchorEpochMs + (monotonicNow - this.anchorMonotonicMs);
  }

  renderTime(epochNowMs, forceAccessibleUpdate = false) {
    const epochSecond = Math.floor(epochNowMs / 1000);
    const millisecond = ((epochNowMs % 1000) + 1000) % 1000;
    const fractionalSecond = Math.floor(millisecond * (10 ** (FRACTION_DIGITS - 3)))
      .toString()
      .padStart(FRACTION_DIGITS, "0");

    this.digitTargets.forEach((digit, index) => {
      digit.textContent = fractionalSecond[index] || "0";
    });

    if (forceAccessibleUpdate || this.accessibleSecond !== epochSecond) {
      const wholeTime = formatDetroitTime(epochNowMs);
      this.accessibleSecond = epochSecond;
      this.wholeTarget.textContent = wholeTime;
      const label = this.isFrozen
        ? `Frozen Detroit time ${wholeTime}. Release to return to live time.`
        : `Current Detroit time ${wholeTime}. Press and hold to freeze.`;
      this.element.setAttribute("aria-label", label);
    }
  }

  syncAnchor() {
    this.anchorEpochMs = Date.now();
    this.anchorMonotonicMs = performance.now();
  }

  cancelFrame() {
    if (this.frameRequest === undefined) return;

    window.cancelAnimationFrame(this.frameRequest);
    this.frameRequest = undefined;
  }

  setFrozenState(frozen) {
    this.isFrozen = frozen;
    this.element.classList.toggle("is-frozen", frozen);
    this.element.setAttribute("aria-pressed", frozen ? "true" : "false");
  }

  isActivationEvent(event) {
    if (event.type === "keydown") return ["Enter", " "].includes(event.key) && !event.repeat;
    if (event.type === "pointerdown") return event.isPrimary !== false && event.button === 0;

    return false;
  }

  isReleaseEvent(event) {
    if (event.type === "keyup") return ["Enter", " "].includes(event.key);

    return ["pointerup", "pointercancel", "blur"].includes(event.type);
  }
}
